import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Cell,
  Scatter,
  ReferenceLine,
  ResponsiveContainer,
  LabelList,
} from "recharts";

/**
 * Comparative Analysis chart — dual-axis grouped bar.
 * Left axis  : "Per Member Per Month" — gray bars.
 * Right axis : "Funding Level %" — coral dots + sky target line.
 *
 * Three methods surface here per Dave's 2026-03-02 spec:
 * Cash Flow (left) · Adjusted Target (middle, labelled "uReserve") ·
 * Threshold (right).
 *
 * 2026-03-02 (Dave, revision) — horizon-context-sensitive:
 *   PMPM         = SUM(contribution y=1..H) / H / members / 12
 *   Low Reserve  = MIN(ending y=1..H)
 *   Funding %    = ending[H] / full_funding_balance[H] × 100
 * All three re-compute client-side by slicing the 30y schedule the
 * backend already returned — no extra network calls.
 *
 * Low-reserve callouts are now rendered as an SVG `<LabelList>` on
 * the bar (position: top) so they align pixel-perfect with each bar's
 * x-center regardless of Recharts' axis-label margin math.
 */
export default function ComparativeAnalysisChart({
  methods,
  targetPct,
  horizon = 30,
  unitCount = 0,
  hasAdjustment = false,
  onDismiss,
  ffbByYear = null,
  priorYearContribution = 0,
}) {
  // 2026-02-28 (Dave) — Viewing-mode toggle. `pmpm` shows Per Member
  // Per Month (existing default); `total` shows the raw yearly $
  // contribution before dividing by members × 12. Session-scoped —
  // resets to Total Year on every mount per Dave's spec.
  const [viewMode, setViewMode] = useState("total");
  const isTotalMode = viewMode === "total";

  // Formatters for the two modes.  Total mode compresses to $K for
  // any value above $999 so the axis + bar labels stay narrow.
  const fmtDollars = (v) => {
    if (!Number.isFinite(v)) return "$0";
    if (isTotalMode) {
      const abs = Math.abs(v);
      if (abs >= 1000) {
        // Round to nearest $K, add thousands separator on the K number.
        const k = Math.round(v / 1000);
        return `$${k.toLocaleString("en-US")}K`;
      }
      return `$${v.toLocaleString("en-US")}`;
    }
    return `$${v}`;
  };
  // Column values: PMPM shows cents; Total keeps its $K format.
  const fmtCents = (v) => {
    const n = Number(v) || 0;
    return isTotalMode ? fmtDollars(Math.round(n)) : `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Data-key resolvers so the render layer stays view-mode-agnostic.
  // Keeps `dataKey=` strings stable across bars/labels/domain calc.
  const keyPriorYr = isTotalMode ? "totalPriorYr" : "pmpmPriorYr";
  const keyY1      = isTotalMode ? "totalY1"      : "pmpmY1";
  const keyAvg     = isTotalMode ? "totalAvg"     : "pmpm";

  // 2026-09-13 (Dave) — Center-column method chooser removed per
  // board feedback.  The chart now always renders the Adjusted
  // Target method in the middle column so the visual is deterministic
  // and matches the plan-as-adopted numbers used everywhere else.
  // `hasAdjustment` is still accepted as a prop for API compatibility
  // with callers but is no longer used.

  // 2026-02-28 (Dave) — First calendar year of the plan, sourced from
  // the first row of any method's schedule.  Used to replace the
  // literal "Year 1" text in the legend with the actual year (e.g.
  // "PMPM 2026") so boards read a concrete year instead of an
  // ordinal label.  Falls back to "Year 1" phrasing if the schedule
  // hasn't landed yet.
  const firstYear = useMemo(() => {
    if (!methods || !Array.isArray(methods)) return null;
    for (const m of methods) {
      if (m && Array.isArray(m.schedule) && m.schedule[0] && m.schedule[0].year) {
        return m.schedule[0].year;
      }
    }
    return null;
  }, [methods]);
  const y1Label = firstYear ? String(firstYear) : "Year 1";

  const chartData = useMemo(() => {
    if (!methods || !Array.isArray(methods)) return [];
    const byKey = Object.fromEntries(methods.map((m) => [m.key, m]));
    // 2026-09-14 (Dave) — Bar-group order reworked to sit under
    // the corresponding data-table columns above the chart:
    //   1. uReserve         → Adjusted Target Method column
    //   2. Threshold Method → Threshold Method column
    //   3. Cashflow Method  → Cash Flow Method column
    const order = [
      // 2026-02-28 (Dave) — Conditional label + coral underline on
      // the leftmost cluster.  When the Year-1 adjustment slider is
      // non-zero the label reads "Target Method - Adjusted" so
      // boards can see at a glance that the middle-cluster's bars
      // reflect a Year-1 nudge (matching the Funding Method
      // Comparison table column header above).  When adjustment is
      // 0 it reads plain "Target Method".  `hasAdjustment` is
      // provided by the caller via the slider state.
      { key: "adjusted_target",  label: hasAdjustment ? "Target Method - Adjusted" : "Target Method" },
      { key: "threshold",        label: "Threshold Method" },
      { key: "cashflow",         label: "Cashflow Method"  },
    ];
    // 2026-03-02 (Dave) — Funding Level % must match the Dashboard's
    // Funding Level Analysis chart at every horizon.  The backend's
    // `schedule[i].full_funding_balance` uses post-replacement
    // convention (age resets to 0 on replacement years) while the
    // Dashboard uses peak convention (age counts the replacement year
    // itself as 100% depreciated).  They agree at year 30 but diverge
    // at any mid-horizon year where an element replaces.  Recomputing
    // FFB here with `computeFullFundingByYearPeak` — the same helper
    // FundingLevelAnalysisChart uses — brings the two charts into
    // exact agreement.
    // ffbByYear is server-computed (calc.ffb_peak_by_year).
    return order.map(({ key, label }) => {
      const m = byKey[key];
      if (!m) return null;
      const schedule = Array.isArray(m.schedule) ? m.schedule : [];
      // Slice to the user-selected horizon window.
      const H = Math.max(1, Math.min(horizon, schedule.length));
      // PMPM (avg + Year 1 + prior year), totals, Funding % and low reserve are server-computed.
      const S = m.display?.horizons?.[String(H)] || {};
      return {
        method: label,
        pmpm: Math.round((S.pmpm_avg || 0) * 100) / 100,
        pmpmY1: Math.round((S.pmpm_y1 || 0) * 100) / 100,
        pmpmPriorYr: Math.round((S.pmpm_prior_year || 0) * 100) / 100,
        totalY1: Math.round((schedule[0] && schedule[0].contribution) || 0),
        totalAvg: Math.round(S.avg_contributions || 0),
        totalPriorYr: Math.round(Number(priorYearContribution || 0)),
        fl: S.funding_pct_alt || 0,
        lowReserve: Math.round(S.low_balance_amount || 0),
        lowYear: S.low_balance_year ?? null,
      };
    }).filter(Boolean);
  }, [methods, horizon, priorYearContribution, hasAdjustment]);

  const targetLinePct = (targetPct || 0) * 100;

  // Y-axis (left) range — 2026-02 (Dave) — pinned to 110 % of the
  // tallest bar visible in the selected horizon.  Considers all three
  // bar series (Year 1, Average, Prior Year) across all rendered
  // methods, then rounds the ceiling up to the nearest clean tick so
  // axis labels stay tidy.  Floor is always 0.
  // 2026-02-28 (Dave) — Rounding granularity switches with view mode:
  //   PMPM (whole-dollar bars)  → round-to-$5
  //   Total (yearly $ bars)     → round-to-$1K
  const [yLeftMin, yLeftMax] = useMemo(() => {
    if (!chartData.length) return [0, 500];
    const vals = chartData.flatMap((d) => [
      Number(d[keyAvg]) || 0,
      Number(d[keyY1]) || 0,
      Number(d[keyPriorYr]) || 0,
    ]);
    const hi = Math.max(0, ...vals);
    const step = isTotalMode ? 1000 : 5;
    const ceil = Math.max(step, Math.ceil((hi * 1.10) / step) * step);
    return [0, ceil];
  }, [chartData, isTotalMode, keyAvg, keyY1, keyPriorYr]);

  const usd = (v) => `$${Number(v).toLocaleString()}`;

  // Three-row white in-bar label (2026-09-13, Dave):
  //   Row 1 (top, underlined) — "PMPM Year 1"     "$xxx"
  //   Row 2                    — "PMPM Average"    "$yyy"
  //   ───── white rule ─────
  //   Row 3 (bottom)           — "PMPM Prior Yr"   "$zzz"
  // Row 3 sits directly beneath the white rule (mirrors the Row 1
  // underline treatment) — no padding gap.
  const PmpmDualLabel = ({ x, y, width, height, index }) => {
    if (x == null || width == null || height == null) return null;
    const d = chartData[index];
    if (!d) return null;
    const inset = 8;
    // 2026-09-13 (Dave) — Cashflow (index 0) block sits ~6 px lower
    // than dead-center so it settles at optically-comfortable
    // vertical position inside the taller Cashflow bar.
    const topPad = index === 0 ? 6 : 0;
    const cy = y + height / 2 + topPad;
    // Vertical layout — Row 2 → underline → Row 3 mirrors the
    // Row 1 → underline → Row 2 spacing exactly.
    //
    //   rowY1        cy − 17    Row 1 baseline
    //   underlineY   cy − 14    (rowY1 + 3)
    //   rowY2        cy −  4    (rowY1 + 13)
    //   ruleY        cy −  1    (rowY2 + 3)   ← mirrors Row 1 gap
    //   rowY3        cy +  9    (rowY2 + 13)  ← mirrors Row 1→2 gap
    const rowY1 = cy - 17;
    const underlineY = rowY1 + 3;
    const rowY2 = cy - 4;
    const ruleY = rowY2 + 3;
    const rowY3 = rowY2 + 13;
    const leftX = x + inset;
    const rightX = x + width - inset;
    return (
      <g>
        {/* Row 1 — PMPM Year 1 · $xxx (underlined) */}
        <text x={leftX}  y={rowY1} fill="#FFFFFF" fontSize={11} fontWeight={600} textAnchor="start">
          {isTotalMode ? `Total ${y1Label}` : `PMPM ${y1Label}`}
        </text>
        <text x={rightX} y={rowY1} fill="#FFFFFF" fontSize={12} fontWeight={700} textAnchor="end">
          {fmtCents(d[keyY1])}
        </text>
        <line
          x1={leftX} y1={underlineY}
          x2={rightX} y2={underlineY}
          stroke="#FFFFFF"
          strokeWidth={1}
        />
        {/* Row 2 — PMPM Average · $yyy */}
        <text x={leftX}  y={rowY2} fill="#FFFFFF" fontSize={11} fontWeight={600} textAnchor="start">
          {isTotalMode ? "Total Average" : "PMPM Average"}
        </text>
        <text x={rightX} y={rowY2} fill="#FFFFFF" fontSize={12} fontWeight={700} textAnchor="end">
          {fmtCents(d[keyAvg])}
        </text>
        {/* White rule separating the horizon-derived rows from the
            Prior-Year fact below. */}
        <line
          x1={leftX} y1={ruleY}
          x2={rightX} y2={ruleY}
          stroke="#FFFFFF"
          strokeWidth={1}
        />
        {/* Row 3 — PMPM Prior Year · $zzz (below the rule) */}
        <text x={leftX}  y={rowY3} fill="#FFFFFF" fontSize={11} fontWeight={600} textAnchor="start">
          {isTotalMode ? "Total Prior Year" : "PMPM Prior Year"}
        </text>
        <text x={rightX} y={rowY3} fill="#FFFFFF" fontSize={12} fontWeight={700} textAnchor="end">
          {fmtCents(d[keyPriorYr])}
        </text>
      </g>
    );
  };

  // Custom x-axis tick — the leftmost column ("Target Method") renders
  // larger than the other two so it carries the visual emphasis as the
  // primary uReserve method.  All three labels share the same bold
  // dark-blue treatment; only the size varies.
  // 2026-02-28 (Dave) — CustomAxisTick unified: previously
  // "Target Method" rendered at fontSize 20 / bold-700 while the
  // other two rendered at fontSize 14 — the larger text extended
  // to the left past its bar cluster, so the label read as
  // horizontally offset from the bars.  All three now render at
  // the same size, centered under their category X (textAnchor
  // middle), so each label sits directly under its bar group.
  //
  // 2026-02-28 (Dave, later) — The leftmost cluster (Target
  // Method / Target Method - Adjusted) now sits under a coral
  // underline so the reader's eye lands on it as the adopted
  // plan.  Underline width is measured against the rendered text
  // via a `getComputedTextLength` call after mount so it hugs the
  // label exactly regardless of the "- Adjusted" suffix.
  const CustomAxisTick = ({ x, y, payload }) => {
    const value = payload?.value || "";
    const isTargetMethod = value.startsWith("Target Method");
    const textRef = React.useRef(null);
    const [textW, setTextW] = React.useState(0);
    React.useEffect(() => {
      if (textRef.current && typeof textRef.current.getComputedTextLength === "function") {
        setTextW(textRef.current.getComputedTextLength());
      }
    }, [value]);
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          ref={textRef}
          x={0}
          y={0}
          dy={16}
          textAnchor="middle"
          fill="#1C4E80"
          fontSize={14}
          fontWeight={700}
        >
          {value}
        </text>
        {isTargetMethod && textW > 0 && (
          <line
            x1={-textW / 2}
            x2={textW / 2}
            y1={22}
            y2={22}
            stroke="#EA6A47"
            strokeWidth={2}
            strokeLinecap="round"
          />
        )}
      </g>
    );
  };

  // Custom dot renderer for the Projected Funding Level % scatter.
  // Recharts passes `cx`, `cy`, and `payload` (the datum) — matches the
  // pattern used by SAPerMemberDot in ActivityChart. Reads `payload.fl`.
  // 2026-09-14 (Dave) — Funding-Level dot repositioned to sit at the
  // TOP of the PMPM Average bar (yAxisId="left", dataKey="pmpm"), NOT
  // at the fl% position on the right axis.  Dot then offsets right
  // past the average bar's right edge so it never overlaps the bar.
  // Bar geometry:  barSize 36 × 3 bars + barGap 4 × 2 → group width
  // 116, centered at category X.  Average bar right edge = center+58,
  // so DOT_X_OFFSET=66 leaves ~8 px of breathing room.
  const DOT_X_OFFSET = 66;
  const FundingLevelDot = ({ cx, cy, payload }) => {
    if (cx == null || cy == null || !payload || payload.fl == null) return null;
    const dotCx = cx + DOT_X_OFFSET;
    return (
      <g>
        <circle
          cx={dotCx}
          cy={cy}
          r={5}
          fill="#EA6A47"
          stroke="#FFFFFF"
          strokeWidth={1.5}
        />
        <text
          x={dotCx + 9}
          y={cy}
          dy={4}
          fill="#1C4E80"
          fontSize={13}
          fontWeight={700}
          textAnchor="start"
        >
          {`${Math.round(payload.fl)}%`}
        </text>
      </g>
    );
  };

  // SVG-rendered "LOW RESERVE BALANCE $X,XXX" label pinned just above
  // the top of the plot area (not the top of each bar). Recharts LabelList
  // content is given the bar's `x`/`width` (used for horizontal centering)
  // and `y` (unused — we substitute a fixed y so callouts never move up
  // or down with bar height). The ComposedChart's `margin.top` reserves
  // the vertical room these labels occupy.
  const LOW_RESERVE_LABEL_Y = 15;   // "LOW RESERVE BALANCE"
  const LOW_RESERVE_VALUE_Y = 35;   // "$X,XXX"
  const LowReserveLabel = ({ x, width, value }) => {
    if (x == null || width == null) return null;
    const cx = x + width / 2;
    return (
      <g>
        <text
          x={cx}
          y={LOW_RESERVE_LABEL_Y}
          textAnchor="middle"
          fill="#0091D5"
          fontSize={14}
          fontWeight={700}
          style={{ letterSpacing: 0.6 }}
        >
          LOW RESERVE BALANCE
        </text>
        <text
          x={cx}
          y={LOW_RESERVE_VALUE_Y}
          textAnchor="middle"
          fill="#1C4E80"
          fontSize={14}
          fontWeight={400}
          fontFamily="IBM Plex Mono, monospace"
        >
          {usd(value)}
        </text>
      </g>
    );
  };

  // 2026-02-28 (Dave) — Deterministic margin calculation.
  //
  // Goal: CENTER of each 3-bar clump sits directly beneath the
  // CENTER of its corresponding data column above — so clumps sit
  // under TARGET METHOD (ADJUSTED) / THRESHOLD METHOD / CASH FLOW
  // METHOD.  FULL FUNDING METHOD (rightmost column) has no clump.
  //
  // Visible table columns (`target` col is hidden per §202):
  //   YEAR 200 | ADJ_TGT 220 | THRESH 210 | CASH_FLOW 210 | FULL 180
  //   → total 1020, scaled proportionally to panel width.  Column
  // CENTERS as fractions of panel border-box:
  //   Adjusted Target center = 310/1020 = 0.3039
  //   Threshold        center = 525/1020 = 0.5147
  //   Cash Flow        center = 735/1020 = 0.7206
  //
  // Panel layout (measured empirically at cw=1817):
  //   PANEL_PAD (15) + LEGEND_COL (280) + SVG (cw − 310) + PANEL_PAD.
  //   Inside SVG, plot rect starts at margin.left + AXIS_WIDTH_L (60)
  //   and ends at SVG_width − margin.right − AXIS_WIDTH_R (60).
  //
  // Plot-rect placement solved from the two constraint equations:
  //   PANEL_PAD + LEGEND_COL + AXIS_WIDTH_L + margin.left + plot_w/6
  //     = 0.3039 × cw               (clump 1 center in panel coords)
  //   PANEL_PAD + LEGEND_COL + AXIS_WIDTH_L + margin.left + 5·plot_w/6
  //     = 0.7206 × cw               (clump 3 center in panel coords)
  // Subtracting gives  4·plot_w/6 = 0.4167 × cw  →  plot_w = 0.625×cw.
  // Solving back gives  margin.left = 0.1998·cw − 355.  margin.right
  // follows from SVG_width = (cw − 310) = margin.left + plot_w +
  // margin.right + 2·AXIS_WIDTH, so  margin.right = 0.1752·cw − 75.
  //
  // At cw=1817 → margin.left ≈ 8, margin.right ≈ 243, plot_w ≈ 1136.
  // Clamped to ≥ 0 so smaller viewports don't request negative
  // margins (alignment degrades gracefully — clumps ride the axes
  // instead of overshooting into the legend).
  const PANEL_PAD = 15;
  const LEGEND_COL = 280;
  const AXIS_WIDTH_L = 60;
  const AXIS_WIDTH_R = 60;
  const outerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(1368);
  useLayoutEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    const measure = () => setContainerWidth(el.getBoundingClientRect().width);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const cw = containerWidth;
  // PLOT_SHIFT (~½ inch) slides the whole plot right without changing its width.
  const PLOT_SHIFT = 48;
  const dynMarginLeft = Math.max(0, Math.round(0.1998 * cw - 355)) + PLOT_SHIFT;
  const dynMarginRight = Math.max(0, Math.round(0.1752 * cw - 75) - PLOT_SHIFT);
  // Values grid shares the plot column's left/right edges, so matching
  // the plot rect (margin + Y-axis width) puts each 1fr cell center under its bar group.
  const valuesGridPaddingLeft = dynMarginLeft + AXIS_WIDTH_L;
  const valuesGridPaddingRight = dynMarginRight + AXIS_WIDTH_R;

  return (
    <div
      ref={outerRef}
      data-testid="comparative-analysis-chart"
      style={{
        width: "100%",
        height: "100%",
        background: "#FFFFFF",
        // 2026-09-14 (Dave) — Panel border trimmed another notch
        // (1 px → 0.5 px) per Dave.  Also shrinks the panel's outer
        // pixel footprint by 1 px on top + bottom, which may be
        // enough to keep the Contributions viewport from tipping
        // into scroll on some browsers.
        border: "0.5px solid #1C4E80",
        borderRadius: "0 0 4px 4px",
        // 2026-02-28 (Dave) — Interim vertical compaction so the
        // whole chart (border → plot → Low Reserve row) fits on
        // one screen without clipping.  Padding trimmed from
        // 18/20/14/20 → 6/14/6/14.
        padding: "6px 14px 6px 14px",
        boxShadow: "0 4px 18px rgba(28,78,128,0.10)",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      {/* Header row — 2026-09-14 (Dave) — Single title "Comparative
          Illustration" replaces the former twin-title layout; the
          right side now carries an "NN Year Horizon" indicator so
          boards see which window is being illustrated at a glance.
          Both labels share the same font/size/color.
          2026-02-28 (Dave) — Interim vertical compaction: title
          size 19 → 14, marginTop 22 → 2, row marginTop -5 → 0 /
          marginBottom 4 → 2. */}
      <div
        className="flex items-center"
        style={{ marginTop: 0, marginBottom: 2 }}
      >
        <div
          className="font-display font-bold flex items-center"
          style={{
            color: "#1C4E80",
            fontSize: 14,
            letterSpacing: 0.2,
            flex: 1,
            marginTop: 2,
            paddingLeft: 44,
            gap: 16,
          }}
        >
          <span>Comparative Illustration</span>
        </div>
        <div
          className="font-display font-bold"
          style={{
            color: "#1C4E80",
            fontSize: 14,
            letterSpacing: 0.2,
            marginTop: 2,
            textAlign: "right",
            paddingRight: 40,
          }}
          data-testid="cac-horizon-indicator"
        >
          {`${Number(horizon) || 0} Year Horizon`}
        </div>
      </div>

      {/* 2026-09-14 (Dave) — Restructured body:
            • Legend moved from bottom-center to a vertical stack on
              the left side of the plot (5 entries stacked).
            • LOW RESERVE BALANCE is now a single left-aligned label
              in a row *below* the plot, with the three method
              values distributed evenly across the plot's inner
              width so each value lands under the bar group above it
              (Adjusted Target / Threshold / Cash Flow).
            • The in-plot SVG LowReserveLabel is no longer used.
       */}
      <div className="flex" style={{ flex: 1, minHeight: 0 }}>
        {/* Left legend column */}
        <div
          data-testid="cac-legend"
          style={{
            width: 280,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            // 2026-02-28 (Dave) — Interim compaction: gap 18 → 8,
            // paddingTop 60 → 12, fontSize 13 → 12 so the 5-item
            // stack fits alongside a shorter plot without pushing
            // the panel past the viewport.
            gap: 8,
            fontSize: 12,
            color: "#1C4E80",
            paddingLeft: 100,
            paddingTop: 12,
            paddingRight: 8,
            whiteSpace: "nowrap",
          }}
        >
          <div className="flex items-center">
            <span style={{ display: "inline-flex", width: 32, flexShrink: 0, alignItems: "center" }}>
              <span style={{ width: 14, height: 10, background: "#B0B7BE", borderRadius: 2 }} />
            </span>
            <span>{isTotalMode ? "Total Prior Year" : "PMPM Prior Year"}</span>
          </div>
          <div className="flex items-center">
            <span style={{ display: "inline-flex", width: 32, flexShrink: 0, alignItems: "center" }}>
              <span style={{ width: 14, height: 10, background: "#0091D5", borderRadius: 2 }} />
            </span>
            <span>{isTotalMode ? `Total ${y1Label}` : `PMPM ${y1Label}`}</span>
          </div>
          <div className="flex items-center">
            <span style={{ display: "inline-flex", width: 32, flexShrink: 0, alignItems: "center" }}>
              <span style={{ width: 14, height: 10, background: "#7A828C", borderRadius: 2 }} />
            </span>
            <span>{isTotalMode ? "Total Average" : "PMPM Average"}</span>
          </div>
          <div className="flex items-center">
            <span style={{ display: "inline-flex", width: 32, flexShrink: 0, alignItems: "center" }}>
              <span style={{ width: 10, height: 10, borderRadius: 999, background: "#EA6A47" }} />
            </span>
            <span>Projected Funding Level %</span>
          </div>
          <div className="flex items-center">
            <span style={{ display: "inline-flex", width: 32, flexShrink: 0, alignItems: "center" }}>
              <span style={{ width: 18, height: 2, background: "#0091D5" }} />
            </span>
            <span>Target Funding Level</span>
          </div>
          {/* 2026-02-28 (Dave) — View-mode toggle relocated from the
              header row to a comfortable slot beneath the last
              legend item.  Session-scoped (resets to Total Year on
              every mount).  The extra top margin gives the pill room
              to breathe against the legend text above it — bumped
              from 8 → 28 (≈ one pill-height) per Dave. */}
          <div
            data-testid="cac-view-mode-toggle"
            style={{
              display: "inline-flex",
              alignSelf: "flex-start",
              marginTop: 39,
              marginLeft: -40,
              border: "1px solid #1C4E80",
              borderRadius: 999,
              overflow: "hidden",
              fontWeight: 600,
              fontSize: 11,
              letterSpacing: 0.3,
            }}
          >
            <button
              type="button"
              data-testid="cac-view-mode-pmpm"
              onClick={() => setViewMode("pmpm")}
              style={{
                padding: "3px 10px",
                background: !isTotalMode ? "#1C4E80" : "#FFFFFF",
                color: !isTotalMode ? "#FFFFFF" : "#1C4E80",
                cursor: "pointer",
                border: "none",
              }}
            >
              Per Member Per Month
            </button>
            <button
              type="button"
              data-testid="cac-view-mode-total"
              onClick={() => setViewMode("total")}
              style={{
                padding: "3px 10px",
                background: isTotalMode ? "#1C4E80" : "#FFFFFF",
                color: isTotalMode ? "#FFFFFF" : "#1C4E80",
                cursor: "pointer",
                border: "none",
                borderLeft: "1px solid #1C4E80",
              }}
            >
              Total Year
            </button>
          </div>
        </div>

        {/* Plot column (fills remaining width).  2026-09-14 (Dave) —
            Height paddings removed; plot fills the body row so the
            chart is as tall as the panel allows. */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chartData}
            // 2026-09-14 (Dave, iteration 4) — margin.left trimmed
            // from 240 → 180 to stretch the plot rect leftward.  This
            // moves the left Y-axis left by ~60 px and shifts the
            // three bar groups left by 50 px (G1), 30 px (G2, ≈ 5
            // char spaces), and 10 px (G3) — landing G1 under
            // Adjusted Target and G2 under Threshold while Cashflow
            // barely moves.
            // 2026-09-14 (Dave) — Margins now computed DYNAMICALLY
            // from the measured container width so the 3 clumps
            // stay under the Adjusted Target / Threshold / Cash
            // Flow columns at ANY viewport width (columns are
            // distributed proportionally by the browser).  See the
            // PLOT_*_RATIO constants at the top of the render.
            // 2026-02-28 (Dave) — Interim vertical compaction:
            // margin.top 32 → 12, margin.bottom 30 → 18 for a
            // shorter plot rect.
            margin={{ top: 12, right: dynMarginRight, bottom: 18, left: dynMarginLeft }}
            barCategoryGap="30%"
          >
            <CartesianGrid stroke="#EEF2F5" vertical={false} />
            <XAxis
              dataKey="method"
              tick={<CustomAxisTick />}
              axisLine={{ stroke: "#C9D6E2" }}
              tickLine={false}
              interval={0}
              // 2026-02-28 (Dave) — Interim: X-axis strip 40 → 28
              // to reclaim ~12 px of vertical space.
              height={28}
            />
            <YAxis
              yAxisId="left"
              width={AXIS_WIDTH_L}
              domain={[yLeftMin, yLeftMax]}
              tick={{ fill: "#4A4A4A", fontSize: 12, fontWeight: 700 }}
              tickFormatter={fmtDollars}
              axisLine={{ stroke: "#C9D6E2" }}
              tickLine={false}
              label={{
                value: isTotalMode ? "Dollars Per Year" : "Dollars Per Month",
                angle: -90,
                position: "insideLeft",
                offset: 5,
                style: {
                  textAnchor: "middle",
                  fill: "#1C4E80",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.3,
                },
              }}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              width={AXIS_WIDTH_R}
              // 2026-09-14 (Dave) — Domain widened 0–50 → 0–100 so
              // Funding Level % values above 50 (Threshold Method at
              // ~70%, Full Funding at 100%) plot on-scale.  Ticks
              // every 20 keep the axis readable without crowding.
              // Styling (font size 12, weight 700, fill) unchanged.
              domain={[0, 100]}
              ticks={[0, 20, 40, 60, 80, 100]}
              tick={{ fill: "#4A4A4A", fontSize: 12, fontWeight: 700 }}
              tickFormatter={(v) => `${v}%`}
              axisLine={{ stroke: "#C9D6E2" }}
              tickLine={false}
              label={{
                value: "Funding Level %",
                angle: 90,
                position: "insideRight",
                offset: 5,
                style: {
                  textAnchor: "middle",
                  fill: "#1C4E80",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.3,
                },
              }}
            />

            {/* Target-funding-level horizontal line (right axis).
                2026-09-14 (Dave) — Inline label removed; the right
                Y-axis already renders "40%" at exactly the same
                height, so the reference-line label was doubling up
                on top of it. */}
            <ReferenceLine
              yAxisId="right"
              y={targetLinePct}
              stroke="#0091D5"
              strokeWidth={1}
              ifOverflow="extendDomain"
            />

            {/* 2026-09-14 (Dave) — Grouped bars: one column each per
                method.  Order left→right: PMPM Prior Year (light
                slate — the historical anchor), PMPM Year 1 (Sky —
                brightest, the current-year ask), PMPM Average
                (mid-slate — horizon average).  LOW RESERVE +
                Scatter dots attach to the *middle* bar (`pmpmY1`)
                so they visually center over the group.  In-bar
                PmpmDualLabel is no longer used — the three values
                are expressed by bar heights + the legend below. */}
            <Bar
              yAxisId="left"
              dataKey={keyPriorYr}
              barSize={isTotalMode ? 36 : 46}
              radius={[3, 3, 0, 0]}
              fill="#B0B7BE"
              isAnimationActive={false}
            >
              <LabelList
                dataKey={keyPriorYr}
                position="insideTop"
                fill="#FFFFFF"
                fontSize={11}
                fontWeight={700}
                formatter={fmtCents}
              />
            </Bar>
            <Bar
              yAxisId="left"
              dataKey={keyY1}
              barSize={isTotalMode ? 36 : 46}
              radius={[3, 3, 0, 0]}
              fill="#0091D5"
              isAnimationActive={false}
            >
              <LabelList
                dataKey={keyY1}
                position="insideTop"
                fill="#FFFFFF"
                fontSize={11}
                fontWeight={700}
                formatter={fmtCents}
              />
            </Bar>
            <Bar
              yAxisId="left"
              dataKey={keyAvg}
              barSize={isTotalMode ? 36 : 46}
              radius={[3, 3, 0, 0]}
              fill="#7A828C"
              isAnimationActive={false}
            >
              <LabelList
                dataKey={keyAvg}
                position="insideTop"
                fill="#FFFFFF"
                fontSize={11}
                fontWeight={700}
                formatter={fmtCents}
              />
            </Bar>

            {/* Coral dot — Projected Funding Level %.  Anchored to
                the RIGHT axis (fl% value) so vertical position is
                driven by the true funding-level number; horizontal
                position is offset right of the PMPM Average bar so
                the marker never overlaps the bars. */}
            <Scatter
              yAxisId="right"
              dataKey="fl"
              shape={<FundingLevelDot />}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
        </div>
      </div>

      {/* 2026-09-14 (Dave) — Low Reserve Balance row.  Sits one row
          higher than before (marginTop pulled negative) so it hugs
          the plot's x-axis strip.  Label left edge is now aligned
          with the legend items above it (paddingLeft 80 to match
          the legend column's paddingLeft).  Each value is rendered
          as "$X,XXX (YYYY)" — the amount plus the calendar year the
          low occurred in — so the reader can see WHEN the reserve
          bottomed out per method without a separate row. */}
      <div
        className="flex items-baseline"
        data-testid="cac-low-reserve-row"
        style={{
          // 2026-02-28 (Dave) — Interim: pull the row a touch
          // tighter (marginTop -18 → -12) and shrink the label /
          // values (14 → 12) so the whole panel — border to Low
          // Reserve row — fits within its overlay envelope.
          marginTop: -12,
          fontSize: 10,
          color: "#1C4E80",
        }}
      >
        <div
          style={{
            width: 280,
            flexShrink: 0,
            paddingLeft: 80,
            fontSize: 12,
            fontWeight: 700,
            color: "#0091D5",
          }}
        >
          Low Reserve Balance (Year)
        </div>
        <div
          style={{
            flex: 1,
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            // Grid mirrors the chart's 3 evenly distributed bar
            // groups; each `1fr` cell centers its value under the
            // corresponding clump.  paddingLeft/paddingRight are now
            // computed dynamically from the same PLOT_*_RATIO
            // constants that drive the chart margins, so the values
            // stay centered under the method labels at ANY viewport.
            paddingLeft: valuesGridPaddingLeft,
            paddingRight: valuesGridPaddingRight,
            // 2026-02-28 (Dave) — Interim compaction: 14 → 12 to
            // match the shorter label.
            fontSize: 12,
            fontWeight: 400,
            color: "#1C4E80",
          }}
        >
          {chartData.map((d, i) => {
            // 2026-09-14 (Dave) — Values now centered directly under
            // each method clump.  Per-cell nudges removed so each
            // `1fr` grid cell centers its value on the category X-
            // coordinate (paddingLeft 180 / paddingRight 60 already
            // match the ComposedChart's margin.left/margin.right so
            // grid centers 1/6·3/6·5/6 line up with the plot's
            // category centers 1/6·3/6·5/6).
            const amt = `$${Number(d.lowReserve || 0).toLocaleString()}`;
            const yr = d.lowYear != null ? d.lowYear : null;
            const atZero = Number(d.lowReserve || 0) <= 0;
            return (
              <span
                key={d.method}
                data-testid={`cac-low-reserve-value-${i}`}
                data-at-zero={atZero ? "true" : "false"}
                style={{ textAlign: "center", ...(atZero ? { color: "#EA6A47", fontWeight: 700 } : {}) }}
              >
                {amt}
                {yr != null && (
                  <>
                    {" "}
                    {/* 2026-09-14 (Dave) — Year tag rendered a shade
                        lighter than the dollar figure so the reader's
                        eye lands on the amount first and the year
                        sits quietly beside it. */}
                    <span style={{ color: atZero ? "#EA6A47" : "rgba(28,78,128,0.55)" }}>
                      ({yr})
                    </span>
                  </>
                )}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}
